import React, { useState } from "react";
import { MdPlaylistAdd, MdDeleteSweep } from "react-icons/md";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

const DualPanelSelector = ({
  title,
  availableItems,
  selectedItems,
  onSelect,
  onUnselect,
  onSelectAll,
  onUnselectAll,
  displayField = "name",
  idField = "_id",
  className = "",
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredItem, setHoveredItem] = useState(null);

  const filteredAvailableItems = availableItems.filter((item) =>
    item?.[displayField].toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className={`bg-gray-50 p-3 rounded ${className}`}>
      <label className="block text-sm font-medium mb-2">{title}</label>
      <div className="flex gap-3">
        {/* Available Items */}
        <div className="flex-1">
          <div className="bg-white rounded border border-gray-200">
            <div className="p-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-3 flex-1">
                <h3 className="text-sm font-medium whitespace-nowrap">
                  Available
                </h3>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder={`Search ${title.toLowerCase()}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onSelectAll}
                disabled={isLoading || availableItems.length === 0}
                title="Select All"
                className="p-1 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded-full ml-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdPlaylistAdd size={20} />
              </button>
            </div>
            <div
              className="overflow-y-auto relative"
              style={{ maxHeight: "250px" }}
            >
              {isLoading ? (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                  <div
                    key={`loadingInclusions`}
                    className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
                  ></div>
                </div>
              ) : (
                <>
                  {filteredAvailableItems?.map((item) => (
                    <div
                      key={item?.[idField]}
                      onClick={() => onSelect(item)}
                      onMouseEnter={() => setHoveredItem(item?.[idField])}
                      onMouseLeave={() => setHoveredItem(null)}
                      className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer flex items-center group border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                    >
                      <span className="flex-1 text-sm">
                        {item?.[displayField]}
                      </span>
                      <FaArrowRight
                        className={`transition-opacity duration-150 text-blue-500 ${hoveredItem === item?.[idField] ? "opacity-100" : "opacity-0"}`}
                      />
                    </div>
                  ))}
                  {filteredAvailableItems.length === 0 && (
                    <div className="p-2 text-center text-sm">
                      {searchQuery
                        ? `No matching ${title.toLowerCase()} found`
                        : `No available ${title.toLowerCase()}`}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Selected Items */}
        <div className="flex-1">
          <div className="bg-white rounded border border-gray-200">
            <div className="p-2 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">Selected</h3>
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                  {selectedItems?.length}
                </span>
              </div>
              <button
                type="button"
                onClick={onUnselectAll}
                disabled={selectedItems?.length === 0}
                title="Remove All"
                className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdDeleteSweep size={20} />
              </button>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: "250px" }}>
              {selectedItems?.map((item) => (
                <div
                  key={item?.[idField]}
                  onClick={() => onUnselect(item)}
                  onMouseEnter={() => setHoveredItem(item?.[idField])}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="px-2 py-1.5 hover:bg-red-50 cursor-pointer flex items-center group border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                >
                  <FaArrowLeft
                    className={`transition-opacity duration-150 text-red-500 ${hoveredItem === item?.[idField] ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="flex-1 text-sm ml-2">
                    {item?.[displayField]}
                  </span>
                </div>
              ))}
              {selectedItems?.length === 0 && (
                <div className="p-2 text-center text-sm">
                  No selected {title.toLowerCase()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualPanelSelector;
